/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useEffect, useCallback } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import { getAssociatedSearchLogs } from '../services'
import {
  DataGrid,
  gridClasses,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import AuditFiltersQualifiers from './AuditFiltersQualifiers'
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  Typography,
  Grid,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'

interface QualifierLogRow {
  readonly id: number
  readonly search_type: string
  readonly search_info: string
  readonly created_at: string
  readonly 'user:representation_name': string
  readonly 'station:name': string
}

// eslint-disable-next-line max-lines-per-function
export default function ListAllAuditQualifiers() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const selectType = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const userId = useHookstate<number | null>(null)
  const resetValeus = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const tableMode = 'server'
  const limit = 15
  const expandQualifiers = useHookstate<boolean>(false)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] = useState<QualifierLogRow | null>(null)

  const formatedValueInfoSearch = (value) => {
    const valueFormated = JSON.parse(value)
    let result = ''
    const keys = Object.keys(valueFormated)
    keys.forEach((key, index) => {
      if (valueFormated.hasOwnProperty(key)) {
        result +=
          translate(key) +
          ': ' +
          (valueFormated[key].name
            ? valueFormated[key].name
            : isDate({ stringDate: valueFormated[key] })
            ? dayjs(valueFormated[key]).format('DD/MM/YYYY')
            : valueFormated[key])

        if (index < keys.length - 1) {
          result += ', \n'
        }
      }
    })
    return result
  }

  const isDate = ({ stringDate }) => {
    return dayjs(stringDate, 'YYYY-MM-DD', true).isValid()
  }

  const handleView = (row: QualifierLogRow) => {
    setSelectedRow(row)
    setOpenModal(true)
  }

  const columns: readonly GridColDef[] = [
    { field: 'id' },
    {
      field: 'search_type',
      headerName: translate('Search Type'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value) => {
        return translate(value)
      },
    },
    {
      field: 'search_info',
      headerName: translate('Search Info'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (value) => {
        return formatedValueInfoSearch(value)
      },
    },
    {
      field: 'user:representation_name',
      headerName: translate('User'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'station:name',
      headerName: translate('Station'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'created_at',
      headerName: translate('Performed at'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value) => {
        return dayjs(value as string).format('DD/MM/YYYY HH:mm')
      },
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Tooltip title={translate('Visualizar')}>
            <IconButton
              onClick={() => handleView(params.row)}
              size="small"
              aria-label={translate('Visualizar')}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const fetchData = useCallback(
    async (sortField?: string, sortOrder?: 'asc' | 'desc') => {
      if (!startDate || !finishDate) return
      loadingButton.set(true)

      const searchLogData = await getAssociatedSearchLogs({
        searchType: selectType.get(),
        startDate: startDate,
        finishDate: finishDate,
        userId: userId.get(),
        sortField,
        sortOrder,
      }).finally(() => {
        loadingButton.set(false)
        expandQualifiers.set(true)
      })

      setRows(
        searchLogData.data.map((element) => ({
          ...element,
          'station:name': element?.station?.name,
          'user:representation_name': element?.user?.representation_name,
          search_info: element?.search_info,
        }))
      )

      setRowCount(searchLogData.rowsCount)
    },
    [startDate, finishDate, selectType, userId, loadingButton, expandQualifiers]
  )

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    selectType.set(null)
    setRows([])
    setSortModel([])
    setPaginationModel({ page: 0, pageSize: limit })
    expandQualifiers.set(false)
  }

  const submit = async () => {
    if (!startDate || !finishDate) {
      notify({
        type: 'error',
        message: translate('Start date and end date must be provided'),
      })
      return false
    }
    setPaginationModel({ page: 0, pageSize: limit })
    await fetchData()
  }

  useEffect(() => {
    if (startDate && finishDate) {
      const sortField = sortModel.length > 0 ? sortModel[0].field : undefined
      const sortOrder =
        sortModel.length > 0 && sortModel[0].sort
          ? (sortModel[0].sort as 'asc' | 'desc')
          : undefined
      fetchData(sortField, sortOrder)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortModel])

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setOpenModal(false)
    }
  }

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditFiltersQualifiers
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          selectType={selectType}
          initialPeriod={initialPeriod}
          focusChange={focusChange}
          resetValues={resetValeus}
          userId={userId}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => submit()}
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() => expandQualifiers.set(!expandQualifiers.get())}
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandQualifiers.value ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandQualifiers.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            columnVisibilityModel={{
              id: false,
              deleted_at: false,
            }}
            disableColumnMenu
            autoHeight
            pagination
            filterMode={tableMode}
            sortingMode={tableMode}
            rowCount={rowCount}
            onSortModelChange={(model) => {
              setSortModel(model)
              setPaginationModel({ page: 0, pageSize: limit })
            }}
            getRowHeight={(params) => {
              if (params.model.search_info.length > 36) {
                return 'auto'
              }
              return null
            }}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <Dialog
        fullWidth
        maxWidth={'md'}
        open={openModal}
        onClose={clickOutModalClose}
        disableEscapeKeyDown={true}
      >
        <DialogTitle
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          {translate('Actions Performed')}
          <IconButton aria-label="close" onClick={() => setOpenModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 5 }}>
          {selectedRow && (
            <Grid container xs={12} display={'flex'} flexDirection={'row'}>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('User')}:
                </Typography>
                <Typography variant="subtitle2">
                  {selectedRow['user:representation_name']}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Consulted origin')}:
                </Typography>
                <Typography variant="subtitle2">
                  {selectedRow['station:name']}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Search Type')}:
                </Typography>
                <Typography variant="subtitle2">
                  {translate(selectedRow.search_type)}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Performed at')}:
                </Typography>
                <Typography variant="subtitle2">
                  {dayjs(selectedRow.created_at).format('DD/MM/YYYY HH:mm')}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
